"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"
import { ThumbsDown, ThumbsUp } from "lucide-react"

export function DocumentationFeedback({ documentationId, articleId }: {
  documentationId: string,
  articleId: string
}) {
  const [feedback, setFeedback] = useState<string>()
  const [rating, setRating] = useState<"HELPFUL" | "NOT_HELPFUL" | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!rating) {
      toast.error("Rating Required",{
        description: "Please select whether this documentation was helpful.",
      })
      return
    }

    setSubmitting(true)

    try {

      const result = fetch('/api/ratings', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          documentationId,
          articleId,
          rating,
          feedback
        })
      }).then(async res => {
        if (!res.ok) throw new Error(await res.json())
        return await res.json()
      }).then(res => setSubmitted(false))
      .finally(() => setSubmitting(false))

      toast.promise(result, {
        success: () => ({
          message: "Feedback Submitted",
          description: "Thank you for your feedback!",
        }),
        error: (error) => ({
          message: "Error",
          description: error
        }),
        loading: 'Submiting feedback...'
      })
      // In a real implementation, you would send the feedback to the server
      // For now, we'll just simulate a submission with a timeout
      await result

      /*toast.success("Feedback Submitted", {
        description: "Thank you for your feedback!",
      })*/

      setSubmitted(true)
    } catch (error: any) {
      console.error("Error submitting feedback:", error)
      toast.error("Error", {
        description: "Failed to submit feedback. Please try again.",
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="rounded-lg border p-4 text-center">
        <h3 className="text-lg font-medium">Thank You!</h3>
        <p className="text-muted-foreground">Your feedback has been submitted.</p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border p-4">
      <h3 className="mb-4 text-lg font-medium">Was this documentation helpful?</h3>
      <div className="mb-4 flex gap-4">
        <Button
          variant={rating === "HELPFUL" ? "secondary" : "outline"}
          className="flex-1"
          onClick={() => setRating("HELPFUL")}
        >
          <ThumbsUp className="mr-2 h-4 w-4" />
          Yes, it was helpful
        </Button>
        <Button
          variant={rating === "NOT_HELPFUL" ? "secondary" : "outline"}
          className="flex-1"
          onClick={() => setRating("NOT_HELPFUL")}
        >
          <ThumbsDown className="mr-2 h-4 w-4" />
          No, it needs improvement
        </Button>
      </div>
      {rating && (
        <div className="space-y-4">
          <Textarea
            placeholder="Do you have any additional feedback? (optional)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
          />
          <Button onClick={handleSubmit} disabled={submitting} className="w-full" variant="secondary">
            {submitting ? "Submitting..." : "Submit Feedback"}
          </Button>
        </div>
      )}
    </div>
  )
}
